<template>
  <div class="dm-editor">

    <div class="dm-head">
      <div>
        <h4 class="dm-title">
          <span class="material-symbols-outlined">swap_horiz</span>
          Renommage des colonnes
        </h4>
        <p class="dm-hint">
          Une ligne par colonne à renommer. À gauche l'intitulé exact tel qu'il
          apparaît dans le fichier, à droite le nom utilisé ensuite. Les colonnes
          absentes de cette liste sont conservées telles quelles.
        </p>
      </div>

      <button type="button" class="dm-raw-toggle" @click="rawMode = !rawMode">
        {{ rawMode ? 'Vue tableau' : 'Vue JSON' }}
      </button>
    </div>

    <!-- Secours : si le JSON existant n'est pas exploitable, on ne le perd pas -->
    <div v-if="parseError" class="dm-error">
      <span class="material-symbols-outlined">warning</span>
      Le contenu enregistré n'est pas un JSON valide. Corrigez-le en vue JSON
      pour reprendre l'édition en tableau.
    </div>

    <div v-if="rawMode || parseError" class="dm-raw">
      <textarea
          class="dm-textarea"
          rows="6"
          :disabled="!editable"
          :value="value || ''"
          placeholder='{"Temp_ext": "TemperatureExterieure"}'
          @input="onRawInput($event.target.value)"
      ></textarea>
    </div>

    <template v-else>
      <div v-if="!rows.length" class="dm-empty">
        Aucun renommage. Les colonnes du fichier sont utilisées telles quelles.
      </div>

      <table v-else class="dm-table">
        <thead>
        <tr>
          <th>Colonne dans le fichier</th>
          <th class="dm-arrow-col"></th>
          <th>Nom cible</th>
          <th class="dm-action-col"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(row, i) in rows" :key="i" :class="{ 'dm-dup': isDuplicate(i) }">
          <td>
            <input
                type="text"
                class="dm-input"
                :disabled="!editable"
                v-model="row.from"
                placeholder="Temp_ext"
                @input="emit"
            />
          </td>
          <td class="dm-arrow">→</td>
          <td>
            <input
                type="text"
                class="dm-input"
                :disabled="!editable"
                v-model="row.to"
                placeholder="TemperatureExterieure"
                @input="emit"
            />
          </td>
          <td class="dm-action-col">
            <button
                v-if="editable"
                type="button"
                class="dm-remove"
                title="Retirer cette ligne"
                @click="removeRow(i)"
            >
              <span class="material-symbols-outlined">close</span>
            </button>
          </td>
        </tr>
        </tbody>
      </table>

      <div class="dm-footer" v-if="editable">
        <button type="button" class="dm-add" @click="addRow">
          <span class="material-symbols-outlined">add</span>
          Ajouter un renommage
        </button>

        <span class="dm-warn" v-if="hasDuplicates">
          Deux lignes renomment la même colonne : seule la dernière s'appliquera.
        </span>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "DataMappingEditor",

  props: {
    // Chaîne JSON, telle que stockée en base : { "colonne_source": "nom_cible" }
    value: {type: String, default: ""},
    editable: {type: Boolean, default: true},
  },

  data() {
    return {
      rows: [],
      rawMode: false,
      parseError: false,
      // Évite de se ré-hydrater sur sa propre émission.
      lastEmitted: null,
    };
  },

  computed: {
    hasDuplicates() {
      const seen = {};

      for (const row of this.rows) {
        const key = (row.from || "").trim();
        if (!key) continue;
        if (seen[key]) return true;
        seen[key] = true;
      }

      return false;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (v === this.lastEmitted) {
          return;
        }
        this.hydrate(v);
      },
    },
  },

  methods: {
    hydrate(raw) {
      this.parseError = false;

      if (!raw || !String(raw).trim()) {
        this.rows = [];
        return;
      }

      try {
        const parsed = JSON.parse(raw);

        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("format inattendu");
        }

        this.rows = Object.keys(parsed).map((k) => ({from: k, to: String(parsed[k])}));
      } catch (e) {
        this.parseError = true;
        this.rows = [];
      }
    },

    isDuplicate(index) {
      const key = (this.rows[index].from || "").trim();
      if (!key) return false;

      return this.rows.some((r, i) => i !== index && (r.from || "").trim() === key);
    },

    addRow() {
      this.rows.push({from: "", to: ""});
    },

    removeRow(i) {
      this.rows.splice(i, 1);
      this.emit();
    },

    emit() {
      const out = {};

      for (const row of this.rows) {
        const from = (row.from || "").trim();
        const to = (row.to || "").trim();

        // Une ligne incomplète n'est pas exportée : elle ne veut rien dire.
        if (!from || !to) continue;

        out[from] = to;
      }

      const json = Object.keys(out).length ? JSON.stringify(out) : null;

      this.lastEmitted = json;
      this.$emit("input", json);
    },

    onRawInput(v) {
      this.lastEmitted = v;
      this.$emit("input", v || null);

      // On retente l'analyse : si ça repasse, la vue tableau redevient possible.
      try {
        if (v && String(v).trim()) {
          JSON.parse(v);
        }
        this.parseError = false;
      } catch (e) {
        this.parseError = true;
      }
    },
  },
};
</script>

<style scoped>
.dm-editor {
  width: 100%;
}

.dm-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.dm-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2d24;
}

.dm-title .material-symbols-outlined {
  font-size: 18px;
}

.dm-hint {
  font-size: 0.78rem;
  color: #8a9a90;
  margin: 5px 0 0;
  max-width: 640px;
}

.dm-raw-toggle {
  background: none;
  border: 1px solid #d8e0db;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.72rem;
  color: #6b7a70;
  cursor: pointer;
  flex-shrink: 0;
}

.dm-raw-toggle:hover {
  border-color: #2d5a3d;
  color: #2d5a3d;
}

.dm-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: #a94442;
  background: #fdf3f2;
  border: 1px solid #f0d6d4;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
}

.dm-error .material-symbols-outlined {
  font-size: 18px;
}

.dm-textarea {
  width: 100%;
  border: 1px solid #d8e0db;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: monospace;
  font-size: 0.78rem;
  color: #1f2d24;
  background: #fff;
  resize: vertical;
}

.dm-textarea:focus {
  outline: none;
  border-color: #2d5a3d;
}

.dm-empty {
  font-size: 0.82rem;
  color: #8a9a90;
  font-style: italic;
  padding: 10px 0;
}

.dm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.dm-table th {
  text-align: left;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a9a90;
  font-weight: 600;
  padding: 4px 6px;
  border-bottom: 1px solid #e6ebe8;
}

.dm-table td {
  padding: 4px 6px;
  vertical-align: middle;
}

.dm-dup .dm-input {
  border-color: #e0b070;
  background: #fffaf2;
}

.dm-arrow-col,
.dm-action-col {
  width: 32px;
}

.dm-arrow {
  text-align: center;
  color: #b0bdb5;
}

.dm-input {
  width: 100%;
  border: 1px solid #d8e0db;
  border-radius: 6px;
  padding: 5px 9px;
  font-size: 0.82rem;
  color: #1f2d24;
  background: #fff;
}

.dm-input:focus {
  outline: none;
  border-color: #2d5a3d;
}

.dm-input:disabled {
  background: #f6f8f7;
  color: #8a9a90;
}

.dm-remove {
  background: none;
  border: none;
  color: #b0bdb5;
  cursor: pointer;
  display: inline-flex;
  padding: 2px;
}

.dm-remove:hover {
  color: #d9534f;
}

.dm-remove .material-symbols-outlined {
  font-size: 18px;
}

.dm-footer {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.dm-add {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px dashed #c8d6cd;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.8rem;
  color: #2d5a3d;
  cursor: pointer;
}

.dm-add:hover {
  border-color: #2d5a3d;
  background: #f6faf7;
}

.dm-add .material-symbols-outlined {
  font-size: 16px;
}

.dm-warn {
  font-size: 0.74rem;
  color: #c9822a;
}
</style>
